import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useItems } from '@/hooks/useItems';
import { useLocations } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import type { Item, Location } from '@/lib/schemas';
import { cn, formatCurrency } from '@/lib/utils';
import { departmentTypeOptions, getStockStatus, getStockStatusLabel, getStockStatusVariant } from '@/lib/seed-data';
import {
  Package,
  Warehouse,
  AlertTriangle,
  TrendingUp,
  Search,
  ArrowRight,
  LayoutGrid,
  List,
  MapPin,
  Box,
  DollarSign,
  Filter,
  X,
} from 'lucide-react';

type ViewMode = 'grid' | 'list';
type LocationFilter = 'all' | 'warehouse' | 'storage' | 'ward' | 'department';
type DepartmentFilter = 'all' | 'general' | 'it';

export default function InventoryPage() {
  const { user } = useAuth();
  const hospitalId = user?.hospital_id || '550e8400-e29b-41d4-a716-446655440000';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [locationTypeFilter, setLocationTypeFilter] = useState<LocationFilter>('all');
  const [departmentFilter, setDepartmentFilter] = useState<DepartmentFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);

  const { data: itemsResponse = { data: [], totalCount: 0 } } = useItems({
    searchQuery: searchQuery || undefined,
    page: 1,
    limit: 1000,
  });
  const { data: locations = [] } = useLocations();
  const { error: toastError } = useToast();

  const items = itemsResponse.data;

  // Group items by location
  const itemsByLocation = useMemo(() => {
    const grouped: Record<string, Item[]> = {};
    items.forEach((item) => {
      const locId = item.location_id || 'unknown';
      if (!grouped[locId]) {
        grouped[locId] = [];
      }
      grouped[locId].push(item);
    });
    return grouped;
  }, [items]);

  // Location stats
  const locationStats = useMemo(() => {
    const stats: Record<string, {
      totalItems: number;
      totalValue: number;
      critical: number;
      low: number;
      normal: number;
      overstock: number;
    }> = {};

    Object.entries(itemsByLocation).forEach(([locId, locItems]) => {
      let critical = 0, low = 0, normal = 0, overstock = 0;
      let totalValue = 0;

      locItems.forEach((item) => {
        const status = getStockStatus(
          item.quantity,
          item.min_stock_level || 0,
          item.max_stock_level || 0,
          item.safety_stock || 0
        );
        if (status === 'critical') critical++;
        else if (status === 'low') low++;
        else if (status === 'overstock') overstock++;
        else normal++;

        totalValue += item.quantity * item.unit_cost;
      });

      stats[locId] = {
        totalItems: locItems.length,
        totalValue,
        critical,
        low,
        normal,
        overstock,
      };
    });

    return stats;
  }, [itemsByLocation]);

  // Filter locations
  const filteredLocations = useMemo(() => {
    let result = locations;

    if (locationTypeFilter !== 'all') {
      result = result.filter((loc) => loc.location_type === locationTypeFilter);
    }

    if (selectedLocation !== 'all') {
      result = result.filter((loc) => loc.location_id === selectedLocation);
    }

    return result;
  }, [locations, locationTypeFilter, selectedLocation]);

  // Filter items for selected location
  const filteredItems = useMemo(() => {
    let result = selectedLocation === 'all' 
      ? items 
      : items.filter((item) => item.location_id === selectedLocation);

    if (departmentFilter !== 'all') {
      result = result.filter((item) => 
        item.department_type === departmentFilter || item.department_type === 'both'
      );
    }

    if (showOnlyIssues) {
      result = result.filter((item) => {
        const status = getStockStatus(
          item.quantity,
          item.min_stock_level || 0,
          item.max_stock_level || 0,
          item.safety_stock || 0
        );
        return status === 'critical' || status === 'low' || status === 'overstock';
      });
    }

    return result;
  }, [items, selectedLocation, departmentFilter, showOnlyIssues]);

  // Global stats
  const globalStats = useMemo(() => {
    let totalItems = 0;
    let totalValue = 0;
    let critical = 0;
    let low = 0;
    let normal = 0;
    let overstock = 0;

    items.forEach((item) => {
      totalItems++;
      totalValue += item.quantity * item.unit_cost;
      const status = getStockStatus(
        item.quantity,
        item.min_stock_level || 0,
        item.max_stock_level || 0,
        item.safety_stock || 0
      );
      if (status === 'critical') critical++;
      else if (status === 'low') low++;
      else if (status === 'overstock') overstock++;
      else normal++;
    });

    return { totalItems, totalValue, critical, low, normal, overstock };
  }, [items]);

  const getLocationName = (locationId: string) => {
    const location = locations.find((l) => l.location_id === locationId);
    return location?.name || 'Unknown';
  };

  const getLocationType = (locationId: string) => {
    const location = locations.find((l) => l.location_id === locationId);
    return location?.location_type || 'unknown';
  };

  const getLocationTypeLabel = (type: string) => {
    switch (type) {
      case 'warehouse': return 'Gudang';
      case 'storage': return 'Storage';
      case 'ward': return 'Ruang/Rawat';
      case 'department': return 'Departemen';
      default: return type;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Inventory Overview
          </h1>
          <p className="text-text-secondary mt-1">
            Stock levels across all locations - {globalStats.totalItems} items, {formatCurrency(globalStats.totalValue)} total value
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'primary' : 'outline'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'primary' : 'outline'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" />
              <span className="text-xs text-text-muted">Total Items</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{globalStats.totalItems}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              <span className="text-xs text-text-muted">Total Value</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{formatCurrency(globalStats.totalValue)}</p>
          </CardContent>
        </Card>
        <Card className={cn(globalStats.critical > 0 ? "border-red-300" : "")}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className={cn("h-4 w-4", globalStats.critical > 0 ? "text-red-600" : "text-gray-400")} />
              <span className="text-xs text-text-muted">Critical</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{globalStats.critical}</p>
          </CardContent>
        </Card>
        <Card className={cn(globalStats.low > 0 ? "border-yellow-300" : "")}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className={cn("h-4 w-4", globalStats.low > 0 ? "text-yellow-600" : "text-gray-400")} />
              <span className="text-xs text-text-muted">Low Stock</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{globalStats.low}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-xs text-text-muted">Normal</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{globalStats.normal}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Warehouse className="h-4 w-4 text-blue-600" />
              <span className="text-xs text-text-muted">Overstock</span>
            </div>
            <p className="text-xl font-bold text-text-primary mt-1">{globalStats.overstock}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[180px]"
            >
              <option value="all">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.location_id} value={loc.location_id!}>
                  {loc.code} - {loc.name}
                </option>
              ))}
            </select>

            <select
              value={locationTypeFilter}
              onChange={(e) => setLocationTypeFilter(e.target.value as LocationFilter)}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[140px]"
            >
              <option value="all">All Types</option>
              <option value="warehouse">Warehouse</option>
              <option value="storage">Storage</option>
              <option value="ward">Ruang</option>
              <option value="department">Department</option>
            </select>

            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value as DepartmentFilter)}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[140px]"
            >
              <option value="all">All Departments</option>
              <option value="general">Umum</option>
              <option value="it">IT</option>
            </select>

            <Button
              variant={showOnlyIssues ? 'destructive' : 'outline'}
              onClick={() => setShowOnlyIssues(!showOnlyIssues)}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showOnlyIssues ? 'Showing Issues' : 'Show Issues Only'}
            </Button>

            {(selectedLocation !== 'all' || departmentFilter !== 'all' || showOnlyIssues || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedLocation('all');
                  setLocationTypeFilter('all');
                  setDepartmentFilter('all');
                  setShowOnlyIssues(false);
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Location Cards - Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredLocations.map((location) => {
            const stats = locationStats[location.location_id!] || {
              totalItems: 0,
              totalValue: 0,
              critical: 0,
              low: 0,
              normal: 0,
              overstock: 0,
            };
            const locItems = itemsByLocation[location.location_id!] || [];
            const filteredLocItems = locItems.filter((item) => {
              if (departmentFilter !== 'all' && item.department_type !== departmentFilter && item.department_type !== 'both') return false;
              if (showOnlyIssues) {
                const status = getStockStatus(item.quantity, item.min_stock_level || 0, item.max_stock_level || 0, item.safety_stock || 0);
                if (status !== 'critical' && status !== 'low' && status !== 'overstock') return false;
              }
              if (searchQuery) {
                const query = searchQuery.toLowerCase();
                if (!item.name.toLowerCase().includes(query) && !item.code.toLowerCase().includes(query)) return false;
              }
              return true;
            });

            if (selectedLocation !== 'all' && filteredLocItems.length === 0) return null;

            return (
              <Card 
                key={location.location_id}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md",
                  selectedLocation === location.location_id ? "ring-2 ring-primary" : "",
                  stats.critical > 0 ? "border-red-300" : stats.low > 0 ? "border-yellow-300" : ""
                )}
                onClick={() => setSelectedLocation(location.location_id!)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-text-muted" />
                      {location.code}
                    </CardTitle>
                    <Badge variant="outline" className="text-xs">
                      {getLocationTypeLabel(location.location_type)}
                    </Badge>
                  </div>
                  <p className="text-sm text-text-secondary">{location.name}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-text-muted text-xs">Items</p>
                      <p className="font-semibold">{stats.totalItems}</p>
                    </div>
                    <div>
                      <p className="text-text-muted text-xs">Value</p>
                      <p className="font-semibold">{formatCurrency(stats.totalValue)}</p>
                    </div>
                    <div className={cn("flex items-center gap-1", stats.critical > 0 ? "text-red-600" : "text-text-muted")}>
                      <AlertTriangle className="h-3 w-3" />
                      <span className="text-xs">Critical: {stats.critical}</span>
                    </div>
                    <div className={cn("flex items-center gap-1", stats.low > 0 ? "text-yellow-600" : "text-text-muted")}>
                      <AlertTriangle className="h-3 w-3" />
                      <span className="text-xs">Low: {stats.low}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Items Table - List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-text-muted">Item</th>
                    <th className="text-left p-4 text-sm font-medium text-text-muted">Location</th>
                    <th className="text-left p-4 text-sm font-medium text-text-muted">Dept</th>
                    <th className="text-center p-4 text-sm font-medium text-text-muted">Qty</th>
                    <th className="text-center p-4 text-sm font-medium text-text-muted">Status</th>
                    <th className="text-right p-4 text-sm font-medium text-text-muted">Min/Max</th>
                    <th className="text-right p-4 text-sm font-medium text-text-muted">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const status = getStockStatus(
                      item.quantity,
                      item.min_stock_level || 0,
                      item.max_stock_level || 0,
                      item.safety_stock || 0
                    );
                    const variant = getStockStatusVariant(status);
                    const label = getStockStatusLabel(status);

                    return (
                      <tr key={item.item_id} className="border-t border-border hover:bg-muted/30">
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-text-primary">{item.name}</p>
                            <p className="text-xs text-text-muted">{item.code} • {item.category}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-text-secondary">
                            {getLocationName(item.location_id)}
                          </span>
                        </td>
                        <td className="p-4">
                          <Badge variant={item.department_type === 'it' ? 'info' : item.department_type === 'both' ? 'success' : 'default'}>
                            {item.department_type === 'general' ? 'Umum' : item.department_type === 'it' ? 'IT' : 'Both'}
                          </Badge>
                        </td>
                        <td className="p-4 text-center">
                          <span className={cn(
                            "font-semibold",
                            status === 'critical' ? 'text-red-600' : status === 'low' ? 'text-yellow-600' : status === 'overstock' ? 'text-blue-600' : 'text-text-primary'
                          )}>
                            {item.quantity}
                            <span className="text-text-muted text-xs ml-1">{item.unit_of_measure}</span>
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <Badge variant={variant}>{label}</Badge>
                        </td>
                        <td className="p-4 text-right text-sm text-text-muted">
                          {item.min_stock_level || 0} / {item.max_stock_level || '-'}
                        </td>
                        <td className="p-4 text-right font-mono text-sm">
                          {formatCurrency(item.quantity * item.unit_cost)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {filteredLocations.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Warehouse className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No locations found</p>
            <p className="text-sm text-text-muted mt-1">
              Add locations in the Locations page to see inventory overview
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
